import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { AgendaPage, DiaryDay, FilterOption } from '@traquility/ui/diary/AgendaPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

interface AgendaPageContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  iconBaseUrl?: string;
  initialData?: {
    diaryDays: DiaryDay[];
    page: number;
    hasMore: boolean;
  };
  fieldIds?: string[];
  dateMax?: string;
}

const AGENDA_QUERY = `
  query AgendaDays($page: Int!, $perPage: Int!, $fieldIds: [ID!], $dateMax: String) {
    agendaDays(page: $page, perPage: $perPage, fieldIds: $fieldIds, dateMax: $dateMax) {
      diaryDays {
        id
        date
        mood
        energy
        note
        activities {
          id
          active
          value
          field {
            id
            name
            icon
            score
          }
        }
      }
      page
      perPage
      hasMore
      totalCount
    }
  }
`;

const CATEGORIES_QUERY = `
  query DiaryCategories {
    diaryCategories {
      id
      name
      fields {
        id
        name
        icon
        score
      }
      fieldSets {
        id
        fields {
          id
          name
          icon
          score
        }
      }
    }
  }
`;

async function fetchAgendaDays({
  endpoint,
  csrfToken,
  page,
  perPage,
  fieldIds,
  dateMax,
}: {
  endpoint: string;
  csrfToken: string;
  page: number;
  perPage: number;
  fieldIds?: string[];
  dateMax?: string;
}) {
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      query: AGENDA_QUERY,
      variables: { page, perPage, fieldIds, dateMax },
    }),
  });

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || 'GraphQL error');
  }

  return result.data.agendaDays;
}

async function fetchCategories({
  endpoint,
  csrfToken,
}: {
  endpoint: string;
  csrfToken: string;
}): Promise<FilterOption[]> {
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
    },
    body: JSON.stringify({
      query: CATEGORIES_QUERY,
    }),
  });

  const result = await response.json();

  if (result.errors) {
    throw new Error(result.errors[0]?.message || 'GraphQL error');
  }

  // Flatten all fields from categories and field sets
  const fields: FilterOption[] = [];
  for (const category of result.data.diaryCategories) {
    // Add direct fields
    for (const field of category.fields) {
      if (field.icon) {
        fields.push({
          id: field.id,
          name: field.name,
          icon: field.icon,
          score: field.score,
        });
      }
    }
    // Add fields from field sets
    for (const fieldSet of category.fieldSets) {
      for (const field of fieldSet.fields) {
        if (field.icon) {
          fields.push({
            id: field.id,
            name: field.name,
            icon: field.icon,
            score: field.score,
          });
        }
      }
    }
  }

  return fields;
}

function AgendaPageInner({
  graphqlEndpoint,
  csrfToken,
  iconBaseUrl = '/assets',
  initialData,
  fieldIds: initialFieldIds = [],
  dateMax,
}: AgendaPageContainerProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [allDiaryDays, setAllDiaryDays] = useState<DiaryDay[]>(initialData?.diaryDays || []);
  const [hasMore, setHasMore] = useState(initialData?.hasMore ?? true);
  const [filteredFieldIds, setFilteredFieldIds] = useState<string[]>(initialFieldIds);
  const [availableFields, setAvailableFields] = useState<FilterOption[]>([]);
  // Track if we've used the initial data (only valid for page 1 with no filters)
  const [usedInitialData, setUsedInitialData] = useState(
    !!(initialData && initialFieldIds.length === 0)
  );

  // Fetch available fields for filtering
  useEffect(() => {
    fetchCategories({ endpoint: graphqlEndpoint, csrfToken })
      .then(setAvailableFields)
      .catch(err => console.error('Failed to fetch categories:', err));
  }, [graphqlEndpoint, csrfToken]);

  // Determine if we should skip the query (only for initial page 1 load with no filters)
  const shouldSkipQuery = usedInitialData && currentPage === 1 && filteredFieldIds.length === 0;

  // Query for fetching agenda days
  const { isFetching } = useQuery({
    queryKey: ['agendaDays', currentPage, filteredFieldIds, dateMax],
    queryFn: async () => {
      const data = await fetchAgendaDays({
        endpoint: graphqlEndpoint,
        csrfToken,
        page: currentPage,
        perPage: 10,
        fieldIds: filteredFieldIds.length > 0 ? filteredFieldIds : undefined,
        dateMax,
      });

      // Append new diary days to existing ones (for pagination)
      if (currentPage === 1) {
        setAllDiaryDays(data.diaryDays);
      } else {
        setAllDiaryDays(prev => {
          // Deduplicate by ID
          const existingIds = new Set(prev.map(d => d.id));
          const newDays = data.diaryDays.filter((d: DiaryDay) => !existingIds.has(d.id));
          return [...prev, ...newDays];
        });
      }

      setHasMore(data.hasMore);
      return data;
    },
    enabled: !shouldSkipQuery,
  });

  // Reset when filter changes
  const handleFilterChange = useCallback((newFieldIds: string[]) => {
    setFilteredFieldIds(newFieldIds);
    setCurrentPage(1);
    setAllDiaryDays([]);
    setHasMore(true);
    setUsedInitialData(false); // Force fetch when filter changes
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setUsedInitialData(false); // Force fetch on load more
      setCurrentPage(prev => prev + 1);
    }
  }, [isFetching, hasMore]);

  const handleDayClick = useCallback((date: string) => {
    // Navigate to diary day page
    window.location.href = `/diary/days/${date}`;
  }, []);

  return (
    <AgendaPage
      diaryDays={allDiaryDays}
      page={currentPage}
      hasMore={hasMore}
      onLoadMore={handleLoadMore}
      onDayClick={handleDayClick}
      isLoading={isFetching}
      filteredFieldIds={filteredFieldIds}
      availableFields={availableFields}
      onFilterChange={handleFilterChange}
      iconBaseUrl={iconBaseUrl}
    />
  );
}

export function AgendaPageContainer(props: AgendaPageContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AgendaPageInner {...props} />
    </QueryClientProvider>
  );
}

export function mountAgendaPage(
  container: HTMLElement,
  props: AgendaPageContainerProps
): Root {
  const root = createRoot(container);
  root.render(<AgendaPageContainer {...props} />);
  return root;
}
